/**
 * โครงสร้างองค์กร (Org Structure) — OWNER/HR. SoT (ADR 0003): PayPilot owns the
 * org tables (departments + positions, with reporting hierarchy). Two tabs:
 *   - แผนก: list + add/edit/delete, shows parent (indented tree)
 *   - ตำแหน่ง: list + add/edit/delete, shows level + department + reports-to
 *
 * Eval Pilot reads these as an overlay (org chart / JD / competency). Salary is
 * NOT shown here (comp lives in its own menu).
 */
import { useMemo, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import {
  useOrgDepartments, useSaveOrgDepartment, useDeleteOrgDepartment,
  useOrgPositions, useSaveOrgPosition, useDeleteOrgPosition,
  type OrgDepartment, type OrgPosition,
} from '../../hooks';
import { Network, Briefcase, Plus, Pencil, Trash2 } from 'lucide-react';

type Tab = 'departments' | 'positions';

/** order departments parent→child, with a depth for indentation. */
function orderDepts(list: OrgDepartment[]): Array<OrgDepartment & { depth: number }> {
  const byParent = new Map<string | null, OrgDepartment[]>();
  for (const d of list) {
    const k = d.parent_department_id ?? null;
    (byParent.get(k) ?? byParent.set(k, []).get(k)!).push(d);
  }
  const out: Array<OrgDepartment & { depth: number }> = [];
  const walk = (parent: string | null, depth: number) => {
    for (const d of (byParent.get(parent) ?? []).sort((a, b) => a.name.localeCompare(b.name, 'th'))) {
      out.push({ ...d, depth });
      walk(d.id, depth + 1);
    }
  };
  walk(null, 0);
  // orphans (parent not in set) — show flat at the end so nothing disappears
  const shown = new Set(out.map((d) => d.id));
  for (const d of list) if (!shown.has(d.id)) out.push({ ...d, depth: 0 });
  return out;
}

export function OwnerOrgStructure() {
  const [tab, setTab] = useState<Tab>('departments');
  return (
    <AdminLayout title="โครงสร้างองค์กร">
      <div className="space-y-4">
        <div className="flex gap-1 border-b border-gray-200">
          <TabBtn active={tab === 'departments'} onClick={() => setTab('departments')} icon={<Network className="w-4 h-4" />}>
            แผนก
          </TabBtn>
          <TabBtn active={tab === 'positions'} onClick={() => setTab('positions')} icon={<Briefcase className="w-4 h-4" />}>
            ตำแหน่ง
          </TabBtn>
        </div>
        {tab === 'departments' ? <DepartmentsTab /> : <PositionsTab />}
      </div>
    </AdminLayout>
  );
}

function TabBtn({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 ${
        active ? 'border-teal-500 text-teal-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

// ── Departments ──────────────────────────────────────────────────────────────

function DepartmentsTab() {
  const { data: depts = [], isLoading } = useOrgDepartments();
  const save = useSaveOrgDepartment();
  const del = useDeleteOrgDepartment();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<OrgDepartment | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{ name: string; parent_department_id: string; description: string }>({
    name: '', parent_department_id: '', description: '',
  });

  const ordered = useMemo(() => orderDepts(depts), [depts]);

  function openCreate() {
    setEditing(null);
    setForm({ name: '', parent_department_id: '', description: '' });
    setShowForm(true);
  }
  function openEdit(d: OrgDepartment) {
    setEditing(d);
    setForm({ name: d.name, parent_department_id: d.parent_department_id ?? '', description: d.description ?? '' });
    setShowForm(true);
  }
  async function onDelete(d: OrgDepartment) {
    const ok = await confirm({ title: `ลบแผนก "${d.name}"?`, danger: true, confirmText: 'ลบ' });
    if (!ok) return;
    del.mutate(d.id, {
      onSuccess: () => toast.success('ลบแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ลบไม่ได้'),
    });
  }
  function onSave() {
    if (!form.name.trim()) { toast.error('กรอกชื่อแผนก'); return; }
    save.mutate(
      { id: editing?.id, name: form.name.trim(), parent_department_id: form.parent_department_id || null, description: form.description.trim() || null },
      {
        onSuccess: () => { toast.success(editing ? 'บันทึกแล้ว' : 'เพิ่มแผนกแล้ว'); setShowForm(false); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่ได้'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button type="button" className="btn-primary inline-flex items-center gap-1.5" onClick={openCreate}>
          <Plus className="w-4 h-4" /> เพิ่มแผนก
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 py-6 text-center">กำลังโหลด…</p>
      ) : ordered.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">ยังไม่มีแผนก — กด “เพิ่มแผนก” เพื่อเริ่ม</p>
      ) : (
        <div className="card divide-y divide-gray-100">
          {ordered.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="flex-1 text-sm text-gray-900" style={{ paddingLeft: `${d.depth * 20}px` }}>
                {d.depth > 0 && <span className="text-gray-300 mr-1">└</span>}
                {d.name}
                {d.description && <span className="text-xs text-gray-400 ml-2">{d.description}</span>}
              </span>
              <button type="button" className="p-1.5 rounded hover:bg-gray-100" aria-label="แก้ไข" onClick={() => openEdit(d)}>
                <Pencil className="w-4 h-4 text-gray-500" />
              </button>
              <button type="button" className="p-1.5 rounded hover:bg-red-50" aria-label="ลบ" onClick={() => void onDelete(d)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'แก้ไขแผนก' : 'เพิ่มแผนก'}
      >
        <div className="space-y-3">
          <Field label="ชื่อแผนก *">
            <input className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="แผนกแม่ (ถ้ามี)">
            <select className="input" value={form.parent_department_id} onChange={(e) => setForm((f) => ({ ...f, parent_department_id: e.target.value }))}>
              <option value="">— ไม่มี (แผนกบนสุด) —</option>
              {depts.filter((d) => d.id !== editing?.id).map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </Field>
          <Field label="คำอธิบาย">
            <input className="input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </Field>
          <div className="flex gap-2 pt-1">
            <button type="button" className="btn-secondary flex-1" onClick={() => setShowForm(false)}>ยกเลิก</button>
            <button type="button" className="btn-primary flex-1" onClick={onSave} disabled={save.isPending}>
              {save.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Positions ────────────────────────────────────────────────────────────────

function PositionsTab() {
  const { data: positions = [], isLoading } = useOrgPositions();
  const { data: depts = [] } = useOrgDepartments();
  const save = useSaveOrgPosition();
  const del = useDeleteOrgPosition();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<OrgPosition | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{ title: string; level: string; department_id: string; parent_position_id: string; description: string }>({
    title: '', level: '1', department_id: '', parent_position_id: '', description: '',
  });

  const deptName = useMemo(() => new Map(depts.map((d) => [d.id, d.name])), [depts]);
  const posTitle = useMemo(() => new Map(positions.map((p) => [p.id, p.title])), [positions]);
  const sorted = useMemo(() => [...positions].sort((a, b) => b.level - a.level || a.title.localeCompare(b.title, 'th')), [positions]);

  function openCreate() {
    setEditing(null);
    setForm({ title: '', level: '1', department_id: '', parent_position_id: '', description: '' });
    setShowForm(true);
  }
  function openEdit(p: OrgPosition) {
    setEditing(p);
    setForm({
      title: p.title, level: String(p.level), department_id: p.department_id ?? '',
      parent_position_id: p.parent_position_id ?? '', description: p.description ?? '',
    });
    setShowForm(true);
  }
  async function onDelete(p: OrgPosition) {
    const ok = await confirm({ title: `ลบตำแหน่ง "${p.title}"?`, danger: true, confirmText: 'ลบ' });
    if (!ok) return;
    del.mutate(p.id, {
      onSuccess: () => toast.success('ลบแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ลบไม่ได้'),
    });
  }
  function onSave() {
    if (!form.title.trim()) { toast.error('กรอกชื่อตำแหน่ง'); return; }
    const level = Number(form.level);
    if (!Number.isInteger(level) || level < 1 || level > 10) { toast.error('ระดับต้องเป็น 1-10'); return; }
    save.mutate(
      {
        id: editing?.id, title: form.title.trim(), level,
        department_id: form.department_id || null,
        parent_position_id: form.parent_position_id || null,
        description: form.description.trim() || null,
      },
      {
        onSuccess: () => { toast.success(editing ? 'บันทึกแล้ว' : 'เพิ่มตำแหน่งแล้ว'); setShowForm(false); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่ได้'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button type="button" className="btn-primary inline-flex items-center gap-1.5" onClick={openCreate}>
          <Plus className="w-4 h-4" /> เพิ่มตำแหน่ง
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 py-6 text-center">กำลังโหลด…</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">ยังไม่มีตำแหน่ง — กด “เพิ่มตำแหน่ง” เพื่อเริ่ม</p>
      ) : (
        <div className="card divide-y divide-gray-100">
          {sorted.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="badge bg-gray-100 text-gray-600 tabular-nums shrink-0">L{p.level}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900">{p.title}</div>
                <div className="text-xs text-gray-400">
                  {p.department_id ? deptName.get(p.department_id) ?? '—' : 'ไม่ระบุแผนก'}
                  {p.parent_position_id && <> · รายงานต่อ {posTitle.get(p.parent_position_id) ?? '—'}</>}
                </div>
              </div>
              <button type="button" className="p-1.5 rounded hover:bg-gray-100" aria-label="แก้ไข" onClick={() => openEdit(p)}>
                <Pencil className="w-4 h-4 text-gray-500" />
              </button>
              <button type="button" className="p-1.5 rounded hover:bg-red-50" aria-label="ลบ" onClick={() => void onDelete(p)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'แก้ไขตำแหน่ง' : 'เพิ่มตำแหน่ง'}
      >
        <div className="space-y-3">
          <Field label="ชื่อตำแหน่ง *">
            <input className="input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="ระดับ (1-10) *">
              <input className="input tabular-nums" type="number" min={1} max={10} value={form.level} onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))} />
            </Field>
            <Field label="แผนก">
              <select className="input" value={form.department_id} onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}>
                <option value="">— ไม่ระบุ —</option>
                {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="รายงานต่อ (ตำแหน่งหัวหน้า)">
            <select className="input" value={form.parent_position_id} onChange={(e) => setForm((f) => ({ ...f, parent_position_id: e.target.value }))}>
              <option value="">— ไม่มี (ตำแหน่งบนสุด) —</option>
              {positions.filter((p) => p.id !== editing?.id).map((p) => (
                <option key={p.id} value={p.id}>{p.title} (L{p.level})</option>
              ))}
            </select>
          </Field>
          <Field label="คำอธิบาย">
            <input className="input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </Field>
          <div className="flex gap-2 pt-1">
            <button type="button" className="btn-secondary flex-1" onClick={() => setShowForm(false)}>ยกเลิก</button>
            <button type="button" className="btn-primary flex-1" onClick={onSave} disabled={save.isPending}>
              {save.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
